import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Edit3,
  Save,
  ShoppingBag,
  Heart,
  MessageSquare,
  Loader2,
  UploadCloud,
  Image as ImageIcon
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import PartSearchSection from "@/components/buyer/PartSearchSection";
import { useDispatch, useSelector } from "react-redux";
import { openAuthDialog } from "../store/auth/authSlice";
import { updateUserProfile } from "../store/auth/authActions";
import ResetPasswordModal from "../components/Modal/ResetPasswordModal";
import { Formik } from "formik";
import * as Yup from "yup";
import { listenToBuyerCount } from "../store/partChat/partChatActions";

// ==================== VALIDATION SCHEMA ====================
const profileValidationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(4, "Name must be at least 4 characters long")
    .max(50, "Name must be 50 characters or less")
    .matches(/^[A-Za-z\s]+$/, "Name can only contain alphabets and spaces")
    .required("Name is required"),

  phone: Yup.string()
    .trim()
    .matches(
      /^(\+1\s?)?(\([2-9][0-9]{2}\)|[2-9][0-9]{2})[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/,
      "Enter a valid US phone number (e.g., 555-123-4567 or +1 555 123 4567)"
    )
    .notRequired()
    .nullable(),

  zipCode: Yup.string()
    .trim()
    .transform((value) => (value === "" ? null : value))
    .matches(/^[0-9-]+$/, "ZIP code can only contain numbers and hyphens")
    .min(4, "ZIP code must be at least 4 characters")
    .max(10, "ZIP code must be at most 10 characters")
    .notRequired()
    .nullable(),
  address: Yup.string()
    .trim()
    .max(100, "Address must be 100 characters or less")
    .notRequired()
    .nullable()
});

// ==================== ANIMATION VARIANTS ====================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, duration: 0.5 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100 }
  }
};

// ==================== PROFILE PICTURE COMPONENT ====================
const ProfilePictureSection = ({
  logoPreview,
  logoUrl,
  isEditing,
  onLogoChange
}) => (
  <div className="space-y-2">
    <Label htmlFor="profileUpload" className="flex items-center">
      <ImageIcon className="mr-2 h-4 w-4 text-muted-foreground" />
      Profile Picture
    </Label>
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 rounded-lg border border-dashed border-muted flex items-center justify-center bg-secondary/30 overflow-hidden">
        {logoPreview || logoUrl ? (
          <img
            src={logoPreview || logoUrl}
            alt="Profile Picture Preview"
            className="w-full h-full object-contain"
          />
        ) : (
          <User className="h-10 w-10 text-muted-foreground" />
        )}
      </div>
      {isEditing && (
        <div>
          <Input
            id="profileUpload"
            name="logoUrl"
            type="file"
            accept="image/*"
            onChange={onLogoChange}
            className="hidden"
          />
          <Label
            htmlFor="profileUpload"
            className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
          >
            <UploadCloud className="mr-2 h-4 w-4" /> Change Picture
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            Max 2MB. Recommended: Square image for best fit.
          </p>
        </div>
      )}
    </div>
  </div>
);

// ==================== FORM FIELD WITH ERROR ====================
const FormField = ({
  id,
  name,
  label,
  icon: Icon,
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  type = "text",
  error,
  touched,
  maxLength
}) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="flex items-center">
      <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
      {label}
    </Label>
    <Input
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={placeholder}
      maxLength={maxLength}
    />
    <AnimatePresence>
      {error && touched && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="text-xs text-red-400"
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

// ==================== PROFILE FORM ACTIONS ====================
const ProfileFormActions = ({
  isEditing,
  isSubmitting,
  isUserUpdateLoading,
  onEdit,
  onCancel,
  showPasswordButton,
  onPasswordClick,
  dirty
}) => {
  return (
    <div className="flex justify-between">
      {isEditing ? (
        <div className="flex gap-2 pt-4">
          <Button
            type="submit"
            className="bg-green-600 hover:bg-green-700"
            disabled={!dirty || isSubmitting || isUserUpdateLoading}
          >
            {(isSubmitting || isUserUpdateLoading) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            <Save className="mr-2 h-4 w-4" /> Save Changes
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting || isUserUpdateLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button type="button" onClick={onEdit} className="mt-2">
          <Edit3 className="mr-2 h-4 w-4" /> Edit Profile
        </Button>
      )}
      {showPasswordButton && (
        <Button
          type="button"
          variant="outline"
          className="mt-3"
          onClick={onPasswordClick}
        >
          <Lock className="mr-2 h-4 w-4" /> Change Password
        </Button>
      )}
    </div>
  );
};

// ==================== PROFILE INFO CARD ====================
const ProfileInfoCard = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  isEditing,
  isSubmitting,
  isUserUpdateLoading,
  logoPreview,
  onLogoChange,
  onEdit,
  onCancel,
  showPasswordButton,
  onPasswordClick,
  dirty
}) => (
  <Card className="shadow-xl border-l-4 border-primary">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <User className="h-6 w-6 text-primary" /> Personal Information
      </CardTitle>
      <CardDescription>
        Manage your contact details and preferences.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-6 pt-6">
      <ProfilePictureSection
        logoPreview={logoPreview}
        logoUrl={values.logoUrl}
        isEditing={isEditing}
        onLogoChange={onLogoChange}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          id="name"
          name="name"
          label="Full Name"
          icon={User}
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={!isEditing}
          placeholder="Your Name"
          error={errors.name}
          touched={touched.name}
        />
        <FormField
          id="email"
          name="email"
          label="Email Address"
          icon={Mail}
          type="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={true}
          placeholder="your.email@example.com"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          id="phone"
          name="phone"
          label="Phone Number"
          icon={Phone}
          type="tel"
          value={values.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={!isEditing}
          placeholder="(123) 456-7890"
          error={errors.phone}
          touched={touched.phone}
        />
        <FormField
          id="address"
          name="address"
          label="Shipping Address"
          icon={MapPin}
          value={values.address}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={!isEditing}
          placeholder="123 Main St, Anytown, USA"
          error={errors.address}
          touched={touched.address}
        />
      </div>

      <FormField
        id="zipCode"
        name="zipCode"
        label="Your ZIP Code (for local search)"
        icon={MapPin}
        value={values.zipCode}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={!isEditing}
        placeholder="e.g., 90210"
        error={errors.zipCode}
        touched={touched.zipCode}
      />

      <ProfileFormActions
        isEditing={isEditing}
        isSubmitting={isSubmitting}
        isUserUpdateLoading={isUserUpdateLoading}
        onEdit={onEdit}
        onCancel={onCancel}
        showPasswordButton={showPasswordButton}
        onPasswordClick={onPasswordClick}
        dirty={dirty}
      />
    </CardContent>
  </Card>
);

// ==================== QUICK ACTION CARD ====================
const QuickActionCard = ({
  title,
  icon: Icon,
  description,
  link,
  showCount,
  count,
  onClick
}) => (
  <Card
    className="hover:shadow-md shadow-lg transition-shadow cursor-pointer border-l-4 border-transparent hover:border-primary"
    onClick={onClick}
  >
    <CardHeader>
      <CardTitle className="flex items-center text-lg">
        <Icon className="mr-2 h-5 w-5 text-primary" />
        {title}
        {showCount && count > 0 && (
          <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
            {count}
          </span>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-sm text-muted-foreground">{description}</p>
    </CardContent>
  </Card>
);

// ==================== QUICK ACTIONS GRID ====================
// const QuickActionsGrid = ({ unreadCount, onNavigate }) => {
//   const quickActions = [
//     // {
//     //   title: "My Orders",
//     //   icon: ShoppingBag,
//     //   description: "Track your purchases and view order history.",
//     //   link: "/profile/orders"
//     // },
//     {
//       title: "My Messages",
//       icon: MessageSquare,
//       description: "View your conversations with sellers.",
//       link: "/profile/messages",
//       showCount: true
//     },
//     {
//       title: "Saved Parts",
//       icon: Heart,
//       description: "View parts you've saved for later.",
//       link: "/profile/saved-parts"
//     }
//   ];

//   return (
//     <motion.div
//       variants={itemVariants}
//       className="grid grid-cols-1 md:grid-cols-2 gap-6"
//     >
//       {quickActions.map((action) => (
//         <QuickActionCard
//           key={action.title}
//           {...action}
//           count={unreadCount}
//           onClick={() => onNavigate(action.link)}
//         />
//       ))}
//     </motion.div>
//   );
// };

// ==================== LOADING STATE ====================
const LoadingState = ({ isAuthenticated }) => (
  <div className="w-full text-center flex flex-col justify-center items-center min-h-[calc(100vh-200px)]">
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h1 className="text-3xl font-bold text-primary mb-4">
        Accessing Buyer Profile
      </h1>
      <p className="text-muted-foreground">
        {!isAuthenticated
          ? "Please log in to continue."
          : "Verifying account..."}
      </p>
    </motion.div>
  </div>
);

// ==================== MAIN COMPONENT ====================
const BuyerProfilePage = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, isUserUpdateLoading } = useSelector(
    (state) => state.auth
  );
  // const { totalBuyerUnreadMessages } = useSelector((state) => state.partChat);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [logoPreview, setLogoPreview] = useState(user?.logoUrl || null);
  const [logoFile, setLogoFile] = useState(null);
  const [isLogoChanged, setIsLogoChanged] = useState(false);

  const handleLogoChange = useCallback(
    (e, setFieldValue) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 2 * 1024 * 1024) {
          toast({
            variant: "destructive",
            title: "File too large",
            description: "Profile image must be under 2MB."
          });
          return;
        }
        if (!file.type.startsWith("image/")) {
          toast({
            variant: "destructive",
            title: "Invalid file type",
            description: "Please upload an image file."
          });
          return;
        }

        setLogoFile(file);
        setIsLogoChanged(true);

        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          setLogoPreview(result);
          setFieldValue("logoUrl", result);
        };
        reader.readAsDataURL(file);
      }
    },
    [toast]
  );

  const handleSaveChanges = async (values, { setSubmitting, resetForm }) => {
    try {
      const { email, ...dataToUpdate } = values;

      if (!isLogoChanged) {
        delete dataToUpdate.logoUrl;
      }

      const resultAction = await dispatch(updateUserProfile(dataToUpdate));

      if (updateUserProfile.fulfilled.match(resultAction)) {
        setIsEditing(false);
        setLogoFile(null);
        setIsLogoChanged(false);

        const updatedUser = resultAction.payload;
        const newFormValues = {
          name: updatedUser.name || "",
          email: updatedUser.email || "",
          phone: updatedUser.phone || "",
          address: updatedUser.address || "",
          zipCode: updatedUser.zipCode || "",
          logoUrl: updatedUser.logoUrl || ""
        };

        resetForm({ values: newFormValues });

        if (updatedUser.logoUrl) {
          setLogoPreview(updatedUser.logoUrl);
        }
      } else {
        throw new Error(resultAction.payload || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Update Failed",
        description:
          error.message || "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = (resetForm, initialValues) => {
    setIsEditing(false);
    setLogoPreview(user?.logoUrl || null);
    setLogoFile(null);
    setIsLogoChanged(false);
    resetForm({ values: initialValues });
  };

  useEffect(() => {
    if (user?.logoUrl && !logoPreview) {
      setLogoPreview(user.logoUrl);
    }
  }, [user?.logoUrl, logoPreview]);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () => navigate("/profile", { replace: true })
        })
      );
    } else if (user && user.role !== "buyer") {
      toast({
        title: "Access Denied",
        description: "This page is for buyers. Redirecting...",
        variant: "destructive"
      });
      navigate(user.role === "seller" ? "/dashboard/profile" : "/");
    }
  }, []);

  if (!isAuthenticated || !user || user.role !== "buyer") {
    return <LoadingState isAuthenticated={isAuthenticated} />;
  }

  const initialFormValues = {
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    address: user.address || "",
    zipCode: user.zipCode || "",
    logoUrl: user.logoUrl || ""
  };

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={profileValidationSchema}
      onSubmit={handleSaveChanges}
      enableReinitialize
      key={user?.uid}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        resetForm,
        setFieldValue,
        dirty
      }) => (
        <motion.div
          className="space-y-8 w-full max-w-4xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.h1
            variants={itemVariants}
            className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-500 to-accent break-words max-w-full"
          >
            Welcome,&nbsp;
            <span className="break-all">{values.name || "Buyer"}</span>!
          </motion.h1>

          {/* <motion.div variants={itemVariants}>
            <PartSearchSection />
          </motion.div> */}

          <form onSubmit={handleSubmit}>
            <motion.div variants={itemVariants}>
              <ProfileInfoCard
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                isEditing={isEditing}
                isSubmitting={isSubmitting}
                isUserUpdateLoading={isUserUpdateLoading}
                logoPreview={logoPreview}
                onLogoChange={(e) => handleLogoChange(e, setFieldValue)}
                onEdit={() => setIsEditing(true)}
                onCancel={() => handleCancelEdit(resetForm, initialFormValues)}
                showPasswordButton={user?.authProvider !== "google"}
                onPasswordClick={() => setResetModalOpen(true)}
                dirty={dirty}
              />
            </motion.div>
          </form>

          {/* <QuickActionsGrid
            unreadCount={totalBuyerUnreadMessages}
            onNavigate={navigate}
          /> */}

          <ResetPasswordModal
            open={resetModalOpen}
            onClose={() => setResetModalOpen(false)}
          />
        </motion.div>
      )}
    </Formik>
  );
};

export default BuyerProfilePage;
